import { AsyncPipe, CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Tree, TreeModule, TreeNodeSelectEvent } from 'primeng/tree';
import { BehaviorSubject, Observable } from 'rxjs';
import { KeyboardFocusDirective } from '@lx/shared/ui/keyboard-focus';
import { CourseStore } from '../../store';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { CourseTreeNode, CourseTreeNodeType, DEFAULT_EXPANDED_COURSE_NODE } from '../../types';
import { SelectableField } from '@lx/quote-tool/lite/shared/types';
import { AutoFocusService } from '@lx/quote-tool/lite/shared/services';

@Component({
  standalone: true,
  selector: 'lx-course-selector',
  templateUrl: './course-selector.component.html',
  styleUrls: ['./course-selector.component.scss'],
  imports: [TreeModule, AsyncPipe, CommonModule, KeyboardFocusDirective],
})
export class CourseSelectorComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('tree') tree: Tree | undefined;
  @Input() queryReset = false;
  @Input() autofocus = false;
  @Output() selectedCourseChanged = new EventEmitter<SelectableField<CourseDetails>>();

  selectedCourse: CourseTreeNode | CourseTreeNode[] | undefined;

  coursesTree$: Observable<CourseTreeNode[]> | undefined;

  private autofocus$ = new BehaviorSubject<boolean>(false);
  private lastValidCourseSelection: CourseTreeNode | undefined;

  private readonly courseStore = inject(CourseStore);
  private readonly autoFocusService = inject(AutoFocusService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.coursesTree$ = this.courseStore.coursesTree$;

    // Initialize the data to be displayed in the component
    this.courseStore.coursesTreeAndSelectedCourse$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ coursesTree, selectedCourse }) => {
      this.initializeSelectedCourse(coursesTree, selectedCourse);
    });
  }

  ngOnChanges({ queryReset, autofocus }: SimpleChanges): void {
    if (queryReset?.currentValue) {
      this.resetFilter();
    }

    if (autofocus?.currentValue !== null && autofocus?.currentValue !== undefined) {
      this.autofocus$.next(autofocus.currentValue);
    }
  }

  ngAfterViewInit(): void {
    this.autoFocusService.focusWhenDesktop(this.autofocus$, () => this.tree?.filterViewChild?.nativeElement);
  }

  /**
   * Handles the onNodeSelect event.
   * - When a parent node (courseType) is clicked: expand/collapse it and restore the last valid course selection
   * - When a child node (course) is clicked: track it and emit the selectedCourseChanged event
   */
  handleNodeSelection(event: TreeNodeSelectEvent) {
    if (this.expandCollapseCourseTypeNode(event.node)) {
      // Parent node clicked - restore the last valid course selection
      this.selectedCourse = this.lastValidCourseSelection;
    } else {
      // Course node clicked - track it and emit event
      this.lastValidCourseSelection = event.node as CourseTreeNode;
      this.selectedCourseChanged.emit({ value: (event.node as CourseTreeNode).courseDetails, fromUser: true });
    }
  }

  /**
   * Initialize the selected course from the value in store. To do so, we need to find the selected course in the tree.
   * If the selected course is in the list of available courses,
   * assign the selected course to the selectedCourseCode variable and expand the node in the tree.
   * As a consequence, collapse the default expanded one.
   *
   * Also, in order to keep everything align, if the course in store is not in the list of available courses, reset the selected course in the store.
   *
   * @param coursesTree the tree with the available courses
   * @param selectedCourseInStore the selected course
   */
  private initializeSelectedCourse(coursesTree: CourseTreeNode[], selectedCourseInStore: CourseDetails) {
    // Get all children courses
    const childrenCourses = coursesTree.flatMap((course) => course.children);

    // Find the course in the list of available courses that matches the selected course in the store
    const course = childrenCourses.find((course) => {
      return course.key === selectedCourseInStore?.code;
    });

    this.selectedCourse = course;
    this.lastValidCourseSelection = course;
    if (this.selectedCourse) {
      // Set the selected course in the store and expand the node in the tree
      coursesTree[DEFAULT_EXPANDED_COURSE_NODE].expanded = false;
      course.parent.expanded = true;
    }
  }

  /**
   * Expands or collapse a node if the type of the node is courseType.
   * Basically, this method is for expanding or collapsing the root or parent nodes (the course types) to show/hide
   * the children nodes (the courses).
   *
   * It returns true if the node type was courseType and false in other case.
   */
  private expandCollapseCourseTypeNode(node: CourseTreeNode) {
    if (node.type === CourseTreeNodeType.courseType) {
      node.expanded = !node.expanded;
    }

    return node.type === CourseTreeNodeType.courseType;
  }

  private resetFilter(): void {
    if (this.tree) {
      this.tree.resetFilter();
    }
  }
}
